import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import { useUploadIndustryBenchmarkDataMutation } from '../../../gql/generated/graphql';

interface ExcelUploadComponentProps {
  onUploadComplete?: (data: any[]) => void;
}

interface ColumnMapping {
  excelColumn: string;
  dbColumn: string;
  sampleData: string;
}

const DB_COLUMNS = [
  { value: 'identifier', label: 'Identifier', required: true },
  { value: 'name', label: 'Company Name', required: false },
  { value: 'url', label: 'Website URL', required: false },
  { value: 'year', label: 'Year', required: true },
  { value: 'date', label: 'Date', required: false },
  { value: 'region', label: 'Region', required: false },
  { value: 'country', label: 'Country', required: false },
  { value: 'sic', label: 'SIC Code', required: true },
  { value: 'ebitda_assets', label: 'EBITDA/Assets', required: false },
  { value: 'one_year_sales_growth', label: '1 Year Sales Growth', required: false },
  { value: 'ev_assets', label: 'EV/Assets', required: false },
  { value: 'ebitda_sales', label: 'EBITDA/Sales', required: false },
  { value: 'z_score', label: 'Z-Score', required: false },
  { value: 'ev_ebitda', label: 'EV/EBITDA', required: false },
];

const STEPS = ['Upload File', 'Select Sheet', 'Map Columns', 'Upload to Database'];

const ExcelUploadComponent: React.FC<ExcelUploadComponentProps> = ({ onUploadComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [excelData, setExcelData] = useState<any[]>([]);
  const [excelColumns, setExcelColumns] = useState<string[]>([]);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<string[]>([]);
  const [uploadIndustryBenchmarkData] = useUploadIndustryBenchmarkDataMutation();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setFile(file);
      parseExcelFile(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
  });

  const parseExcelFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      const data = e.target?.result;
      const wb = XLSX.read(data, { type: 'binary' });
      setWorkbook(wb);

      // If there's only one sheet, select it automatically
      if (wb.SheetNames.length === 1) {
        setSelectedSheet(wb.SheetNames[0]);
        loadSheetData(wb, wb.SheetNames[0]);
      } else {
        // Move to sheet selection step
        setActiveStep(1);
      }
    };
    reader.readAsBinaryString(file);
  };

  const loadSheetData = (wb: XLSX.WorkBook, sheetName: string) => {
    const worksheet = wb.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (jsonData.length > 0) {
      const headers = jsonData[0] as string[];
      const rows = jsonData.slice(1);

      setExcelColumns(headers);
      setExcelData(rows);

      // Initialize column mappings with smart defaults
      const initialMappings = headers.map((header, index) => {
        const sampleData = rows.length > 0 && rows[0][index] ? String(rows[0][index]) : '';
        const mapping: ColumnMapping = {
          excelColumn: header,
          dbColumn: '',
          sampleData,
        };
        return mapping;
      });

      // Track which DB columns have already been mapped to avoid duplicates
      const mappedDbColumns = new Set<string>();

      // Apply smart mapping based on column names
      initialMappings.forEach(mapping => {
        const lowerHeader = mapping.excelColumn.toLowerCase();
        let suggestedDbColumn = '';

        // Smart mapping based on column names
        if ((lowerHeader.includes('identifier') || lowerHeader.includes('id')) && !mappedDbColumns.has('identifier')) {
          suggestedDbColumn = 'identifier';
        } else if ((lowerHeader.includes('name') || lowerHeader.includes('company')) && !mappedDbColumns.has('name')) {
          suggestedDbColumn = 'name';
        } else if ((lowerHeader.includes('url') || lowerHeader.includes('website')) && !mappedDbColumns.has('url')) {
          suggestedDbColumn = 'url';
        } else if (lowerHeader.includes('year') && !mappedDbColumns.has('year')) {
          suggestedDbColumn = 'year';
        } else if (lowerHeader.includes('date') && !mappedDbColumns.has('date')) {
          suggestedDbColumn = 'date';
        } else if (lowerHeader.includes('region') && !mappedDbColumns.has('region')) {
          suggestedDbColumn = 'region';
        } else if (lowerHeader.includes('country') && !mappedDbColumns.has('country')) {
          suggestedDbColumn = 'country';
        } else if (lowerHeader.includes('sic') && !mappedDbColumns.has('sic')) {
          suggestedDbColumn = 'sic';
        } else if (
          lowerHeader.includes('ebitda') &&
          lowerHeader.includes('assets') &&
          !mappedDbColumns.has('ebitda_assets')
        ) {
          suggestedDbColumn = 'ebitda_assets';
        } else if (
          lowerHeader.includes('sales') &&
          lowerHeader.includes('growth') &&
          !mappedDbColumns.has('one_year_sales_growth')
        ) {
          suggestedDbColumn = 'one_year_sales_growth';
        } else if (lowerHeader.includes('ev') && lowerHeader.includes('assets') && !mappedDbColumns.has('ev_assets')) {
          suggestedDbColumn = 'ev_assets';
        } else if (
          lowerHeader.includes('ebitda') &&
          lowerHeader.includes('sales') &&
          !mappedDbColumns.has('ebitda_sales')
        ) {
          suggestedDbColumn = 'ebitda_sales';
        } else if (lowerHeader.includes('z') && lowerHeader.includes('score') && !mappedDbColumns.has('z_score')) {
          suggestedDbColumn = 'z_score';
        } else if (lowerHeader.includes('ev') && lowerHeader.includes('ebitda') && !mappedDbColumns.has('ev_ebitda')) {
          suggestedDbColumn = 'ev_ebitda';
        }

        if (suggestedDbColumn) {
          mapping.dbColumn = suggestedDbColumn;
          mappedDbColumns.add(suggestedDbColumn);
        }
      });

      setColumnMappings(initialMappings);
      // Skip to mapping step (step 2)
      setActiveStep(2);
    }
  };

  const handleSheetSelection = (sheetName: string) => {
    setSelectedSheet(sheetName);
    if (workbook) {
      loadSheetData(workbook, sheetName);
    }
  };

  const handleColumnMappingChange = (index: number, dbColumn: string) => {
    const newMappings = [...columnMappings];
    newMappings[index].dbColumn = dbColumn;
    setColumnMappings(newMappings);
  };

  const validateMappings = () => {
    const errors: string[] = [];
    const requiredColumns = DB_COLUMNS.filter(col => col.required);
    const mappedDbColumns = columnMappings.map(m => m.dbColumn).filter(col => col !== '');

    requiredColumns.forEach(reqCol => {
      if (!mappedDbColumns.includes(reqCol.value)) {
        errors.push(`Required column "${reqCol.label}" is not mapped`);
      }
    });

    // Check for duplicate mappings
    const duplicates = mappedDbColumns.filter((col, index) => mappedDbColumns.indexOf(col) !== index);
    if (duplicates.length > 0) {
      errors.push(`Duplicate mappings found for: ${duplicates.join(', ')}`);
    }

    setErrors(errors);
    return errors.length === 0;
  };

  const handleNext = () => {
    if (activeStep === 2) {
      // Validate mappings before moving to upload
      if (!validateMappings()) {
        return;
      }
      // Go directly to upload
      handleUpload();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const transformDataForUpload = () => {
    const transformedData = excelData.map(row => {
      const record: any = {};
      columnMappings.forEach((mapping, index) => {
        if (mapping.dbColumn) {
          let value = row[index];

          // Skip null, undefined, empty strings, or '--' values
          if (value === null || value === undefined || value === '' || value === '--') {
            return;
          }

          // Type conversions
          if (mapping.dbColumn === 'year' || mapping.dbColumn === 'sic') {
            value = parseInt(value, 10);
            if (isNaN(value)) return;
          } else if (
            ['ebitda_assets', 'one_year_sales_growth', 'ev_assets', 'ebitda_sales', 'z_score', 'ev_ebitda'].includes(
              mapping.dbColumn
            )
          ) {
            value = parseFloat(value);
            if (isNaN(value)) return;
          } else if (mapping.dbColumn === 'date') {
            // Handle Excel date serial numbers
            if (typeof value === 'number') {
              // Excel date serial number - convert to string date
              const excelEpoch = new Date(1899, 11, 30);
              const date = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
              value = date.toISOString().split('T')[0]; // YYYY-MM-DD format
            } else if (typeof value === 'string' && value) {
              // If it's already a string, ensure it's in YYYY-MM-DD format
              value = value.trim();
            }
          }

          // Convert snake_case to camelCase for GraphQL
          const camelCaseKey = mapping.dbColumn.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
          record[camelCaseKey] = value;
        }
      });
      return record;
    });
    return transformedData.filter(record => Object.keys(record).length > 0); // Filter out empty records
  };

  const handleUpload = async () => {
    setUploadStatus('uploading');
    setUploadProgress(0);
    setActiveStep(3); // Move to upload status step

    try {
      const data = transformDataForUpload();
      console.log(`Uploading ${data.length} records...`);
      console.log('Sample data being uploaded:', data.slice(0, 2));

      // Upload data in batches to show progress
      const batchSize = Math.ceil(data.length / 10);
      let uploaded = 0;

      for (let i = 0; i < data.length; i += batchSize) {
        const batch = data.slice(i, i + batchSize);

        const result = await uploadIndustryBenchmarkData({
          variables: { data: batch },
        });

        if (result.errors) {
          console.error('GraphQL errors:', result.errors);
          throw new Error(result.errors[0].message);
        }

        if (result.data?.uploadIndustryBenchmarkData?.errors?.length) {
          setErrors(prev => [...prev, ...result.data.uploadIndustryBenchmarkData.errors]);
        }

        // Log the result for debugging
        console.log('Upload batch result:', result.data?.uploadIndustryBenchmarkData);

        uploaded += batch.length;
        setUploadProgress(Math.round((uploaded / data.length) * 100));
      }

      // Check if there were any errors
      if (errors.length > 0) {
        setUploadStatus('error');
      } else {
        setUploadStatus('success');
        if (onUploadComplete) {
          onUploadComplete(data);
        }
      }
    } catch (error: any) {
      setUploadStatus('error');
      const errorMessage = error.graphQLErrors?.[0]?.message || error.message || 'Upload failed';
      setErrors([errorMessage]);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Paper
              {...getRootProps()}
              sx={{
                p: 4,
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'grey.300',
                backgroundColor: isDragActive ? 'action.hover' : 'grey.50',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.3s',
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {isDragActive ? 'Drop the file here' : 'Drag & drop an Excel file here'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                or click to select a file
              </Typography>
              {file && <Chip label={file.name} onDelete={() => setFile(null)} sx={{ mt: 2 }} color="primary" />}
            </Paper>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Sheet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your Excel file contains multiple sheets. Please select the sheet containing industry benchmark data.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Select Sheet</InputLabel>
              <Select value={selectedSheet} onChange={e => handleSheetSelection(e.target.value)} label="Select Sheet">
                {workbook?.SheetNames.map(sheetName => (
                  <MenuItem key={sheetName} value={sheetName}>
                    {sheetName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Map Columns
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Map database fields to Excel columns. Required fields are marked with *. We've suggested mappings based on
              column names.
            </Alert>
            <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width="30%">Database Field</TableCell>
                    <TableCell width="40%">Excel Column</TableCell>
                    <TableCell width="30%">Sample Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {DB_COLUMNS.map(dbCol => {
                    // Find existing mapping for this DB column
                    const mappingIndex = columnMappings.findIndex(m => m.dbColumn === dbCol.value);
                    const currentExcelColumn = mappingIndex >= 0 ? columnMappings[mappingIndex].excelColumn : '';

                    return (
                      <TableRow key={dbCol.value}>
                        <TableCell>
                          <Typography variant="body2">
                            {dbCol.label} {dbCol.required && <span style={{ color: 'red' }}>*</span>}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={currentExcelColumn}
                              onChange={e => {
                                const newMappings = [...columnMappings];

                                // Clear any existing mapping to this DB column
                                newMappings.forEach(mapping => {
                                  if (mapping.dbColumn === dbCol.value) {
                                    mapping.dbColumn = '';
                                  }
                                });

                                // If a column is selected (not empty)
                                if (e.target.value) {
                                  // Find the Excel column and update its DB mapping
                                  const excelColIndex = newMappings.findIndex(m => m.excelColumn === e.target.value);
                                  if (excelColIndex >= 0) {
                                    // Clear any previous DB column this Excel column was mapped to
                                    newMappings[excelColIndex].dbColumn = dbCol.value;
                                  }
                                }

                                setColumnMappings(newMappings);
                              }}
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Not mapped</em>
                              </MenuItem>
                              {excelColumns.map(col => (
                                <MenuItem key={col} value={col}>
                                  {col}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          {currentExcelColumn && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {columnMappings.find(m => m.excelColumn === currentExcelColumn)?.sampleData || '-'}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </Alert>
            )}
          </Box>
        );

      case 3:
        return (
          <Box sx={{ textAlign: 'center' }}>
            {uploadStatus === 'uploading' && (
              <>
                <Typography variant="h6" gutterBottom>
                  Uploading data...
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {uploadProgress}% complete
                </Typography>
              </>
            )}
            {uploadStatus === 'success' && (
              <>
                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" color="success.main">
                  Upload completed successfully!
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {excelData.length} records have been uploaded to the database.
                </Typography>
              </>
            )}
            {uploadStatus === 'error' && (
              <>
                <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                <Typography variant="h6" color="error.main">
                  Upload failed
                </Typography>
                {errors.map((error, index) => (
                  <Typography key={index} variant="body2" color="error" sx={{ mt: 1 }}>
                    {error}
                  </Typography>
                ))}
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: 400 }}>{renderStepContent()}</Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={handleBack} disabled={activeStep === 0 || uploadStatus === 'uploading'} variant="outlined">
          Back
        </Button>
        <Button
          onClick={handleNext}
          disabled={
            (activeStep === 0 && !file) ||
            (activeStep === 1 && !selectedSheet) ||
            (activeStep === 2 && errors.length > 0) ||
            uploadStatus === 'uploading' ||
            uploadStatus === 'success'
          }
          variant="contained"
        >
          {activeStep === 2 ? 'Import Data' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

export default ExcelUploadComponent;
